import {
  Page,
  StyleSheet,
  View,
  Image,
  Text,
  Svg,
  Path,
} from '@react-pdf/renderer';
import {
  formatDateToString,
  formatNumber,
  getHomeTypeLanguageString,
  getOwnershipLanguageString,
} from '@/helpers';
import strings from '@/language/Swedish';
import { AdsMarketResponseType } from '@/types/apiTypes';
import { MarketDataState } from '@/context/FilterContext';
import Header from '@/components/pdf/components/Header';
import Footer from '@/components/pdf/components/footer';
import { colorsTop10 } from './defaultsForCharts';
import { UserCustomerType } from '@/types/apiTypes';

const styles = StyleSheet.create({
  wrapper: {
    padding: '6mm',
    paddingTop: 0,
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    gap: '6mm',
  },
  filterListContainer: {
    display: 'flex',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '16px',
  },
  filterListItemStyles: {
    display: 'flex',
    alignItems: 'baseline',
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: '2px',
    marginTop: '6px',
  },
  filterListItemLeftTest: {
    marginLeft: '2px',
    fontSize: '11px',
    fontFamily: 'Chivo',
    fontWeight: 400,
    color: 'rgb(100 116 139)',
    width: '112px',
  },
  filterListItemRightTest: {
    fontSize: '11px',
    fontFamily: 'Chivo',
    fontWeight: 400,
  },
});

interface InfoPageProps {
  projectName: string | null;
  colors?: string[];
  marketData: MarketDataState;
  data: AdsMarketResponseType;
  hideClientData?: boolean;
  username: string;
  marketImage: string;
  customer: UserCustomerType;
}
const MarketsPage = ({
  projectName,
  marketData,
  data,
  hideClientData,
  username,
  marketImage,
  customer
}: InfoPageProps) => {

  const sortedData = data.ads_market_shares.sort(
    (a, b) => b.market_share - a.market_share
  );

  const sumOfOthersData =
    sortedData.length > 10
      ? data.ads_market_shares
          .slice(10)
          .reduce((acc, item) => acc + item.market_share, 0)
      : 0;

  const listData = [
    ...data.ads_market_shares.slice(0, 10),
    { market_share: sumOfOthersData, firm: 'Other' },
  ];

  const adDateString =
    marketData.filterData.registered_date_min !== null &&
    marketData.filterData.registered_date_max !== null &&
    marketData.filterData.registered_date_min !== undefined &&
    marketData.filterData.registered_date_max !== undefined
      ? `${formatDateToString(
          new Date(marketData.filterData.registered_date_min)
        )} - ${formatDateToString(
          new Date(marketData.filterData.registered_date_max)
        )}`
      : '';
  return (
    <Page size="A4" orientation="landscape">
      <Header title={projectName} bgColor={customer?.primary_color} logo={customer?.logo_url} />
      <View style={styles.wrapper}>
        <View style={{ width: '50%' }}>
          <View style={styles.filterListContainer}>
            <View>
              <View style={styles.filterListItemStyles}>
                <Svg
                  width="10"
                  height="10"
                  viewBox="0 0 256 256"
                  style={{ transform: 'translateY(-2px)' }}>
                  <Path
                    fill="#64748b"
                    d="M135.008 184H63.504l28.16 54.592a32 32 90 1056.88-29.296ZM235.056.608a8 8 90 00-8.72 1.728C225.888 2.8 180.064 48 136 48h-80a56 56 90 000 112h80c44.064 0 89.888 45.2 90.336 45.664A8 8 90 00240 200V8A8 8 90 00235.056.608Z"></Path>
                </Svg>
                <Text style={styles.filterListItemLeftTest}>
                  {strings.market.ads}:
                </Text>
                <View>
                  <Text style={styles.filterListItemRightTest}>{data.total_ads}</Text>
                </View>
              </View>
              {marketData.filterData.home_type && (
                <View style={styles.filterListItemStyles}>
                  <Svg
                    width="10"
                    height="10"
                    viewBox="0 0 256 256"
                    style={{ transform: 'translateY(-2px)' }}>
                    <Path
                      fill="#64748b"
                      d="M80 112V72C80 69.8783 80.8429 67.8434 82.3431 66.3431C83.8434 64.8429 85.8783 64 88 64H168C170.122 64 172.157 64.8429 173.657 66.3431C175.157 67.8434 176 69.8783 176 72V112H224V32C224 27.7565 222.314 23.6869 219.314 20.6863C216.313 17.6857 212.243 16 208 16H48C43.7565 16 39.6869 17.6857 36.6863 20.6863C33.6857 23.6869 32 27.7565 32 32V112H80Z M240 144H16C11.7565 144 7.68687 145.686 4.68629 148.686C1.68571 151.687 0 155.757 0 160V224C0 228.243 1.68571 232.313 4.68629 235.314C7.68687 238.314 11.7565 240 16 240C20.2435 240 24.3131 238.314 27.3137 235.314C30.3143 232.313 32 228.243 32 224V208H224V224C224 228.243 225.686 232.313 228.686 235.314C231.687 238.314 235.757 240 240 240C244.243 240 248.313 238.314 251.314 235.314C254.314 232.313 256 228.243 256 224V160C256 155.757 254.314 151.687 251.314 148.686C248.313 145.686 244.243 144 240 144Z"></Path>
                  </Svg>
                  <Text style={styles.filterListItemLeftTest}>
                    {strings.market.housingType}:
                  </Text>
                  <View>
                    {marketData.filterData.home_type.map((item) => (
                      <Text key={item} style={styles.filterListItemRightTest}>
                        {getHomeTypeLanguageString(item)}
                      </Text>
                    ))}
                  </View>
                </View>
              )}
              {marketData.filterData.ownership_type && (
                <View style={styles.filterListItemStyles}>
                  <Svg
                    width="10"
                    height="10"
                    viewBox="0 0 256 256"
                    style={{ transform: 'translateY(-2px)' }}>
                    <Path
                      fill="#64748b"
                      d="M206.544 0C204.4 0 202.352 0.864 200.848 2.384L104.544 100.24C96.768 97.712 88.624 96 80 96C35.824 96 0 131.824 0 176C0 220.176 35.824 256 80 256C124.176 256 160 220.176 160 176C160 166.928 158.16 158.352 155.376 150.208L173.872 130.304C175.248 128.832 176.016 126.88 176.016 124.864V96H200.016C204.432 96 208.016 92.416 208.016 88V64H236.704C238.832 64 240.864 63.152 242.368 61.664L253.68 50.352C255.184 48.848 256.016 46.816 256.016 44.688V8C256.016 3.584 252.432 0 248.016 0H206.544ZM72 192C58.752 192 48 181.248 48 168C48 154.752 58.752 144 72 144C85.248 144 96 154.752 96 168C96 181.248 85.248 192 72 192Z"></Path>
                  </Svg>
                  <Text style={styles.filterListItemLeftTest}>
                    {strings.market.ownership}:
                  </Text>
                  <View>
                    {marketData.filterData.ownership_type.map((item) => (
                      <Text key={item} style={styles.filterListItemRightTest}>
                        {getOwnershipLanguageString(item)}
                      </Text>
                    ))}
                  </View>
                </View>
              )}
              {marketData.filterData.registered_date_min &&
                marketData.filterData.registered_date_max && (
                  <View style={styles.filterListItemStyles}>
                    <Svg
                      width="10"
                      height="10"
                      viewBox="0 0 256 256"
                      style={{ transform: 'translateY(-2px)' }}>
                      <Path
                        fill="#64748b"
                        d="M224 48H208V16C208 11.7565 206.314 7.68687 203.314 4.68629C200.313 1.68571 196.243 0 192 0C187.757 0 183.687 1.68571 180.686 4.68629C177.686 7.68687 176 11.7565 176 16V48H80V16C80 11.7565 78.3143 7.68687 75.3137 4.68629C72.3131 1.68571 68.2435 0 64 0C59.7565 0 55.6869 1.68571 52.6863 4.68629C49.6857 7.68687 48 11.7565 48 16V48H32C23.5131 48 15.3737 51.3714 9.37258 57.3726C3.37142 63.3737 0 71.5131 0 80V224C0 232.487 3.37142 240.626 9.37258 246.627C15.3737 252.629 23.5131 256 32 256H224C232.487 256 240.626 252.629 246.627 246.627C252.629 240.626 256 232.487 256 224V80C256 71.5131 252.629 63.3737 246.627 57.3726C240.626 51.3714 232.487 48 224 48ZM32 224V112H224V224H32Z"></Path>
                    </Svg>
                    <Text style={styles.filterListItemLeftTest}>
                      {strings.market.adDate}:
                    </Text>
                    <View>
                      <Text style={styles.filterListItemRightTest}>
                        {adDateString}
                      </Text>
                    </View>
                  </View>
                )}
              {marketData.filterData.price_max && (
                <View style={styles.filterListItemStyles}>
                  <Svg
                    width="10"
                    height="10"
                    viewBox="0 0 256 256"
                    style={{ transform: 'translateY(-2px)' }}>
                    <Path
                      fill="#64748b"
                      d="M128 96C174.4 96 224 81.6 224 48C224 14.4 174.4 0 128 0C81.6 0 32 14.4 32 48C32 81.6 81.6 96 128 96Z M32 104V128C32 161.6 81.6 176 128 176C174.4 176 224 161.6 224 128V104C201.6 118.4 168 128 128 128C88 128 54.4 118.4 32 104Z M32 184V208C32 241.6 81.6 256 128 256C174.4 256 224 241.6 224 208V184C201.6 200 168 208 128 208C88 208 54.4 198.4 32 184Z"></Path>
                  </Svg>
                  <Text style={styles.filterListItemLeftTest}>
                    {strings.filter.price}:
                  </Text>
                  <View>
                    <Text style={styles.filterListItemRightTest}>
                      {`${marketData.filterData.price_min}-${marketData.filterData.price_max} ${strings.filter.price_unit}`}
                    </Text>
                  </View>
                </View>
              )}
              {marketData.filterData.living_area_max && (
                <View style={styles.filterListItemStyles}>
                  <Svg
                    width="10"
                    height="10"
                    viewBox="0 0 256 256"
                    style={{ transform: 'translateY(-2px)' }}>
                    <Path
                      fill="#64748b"
                      d="M50.452 86.646c2.795-3.016 4.984-3.21 18.586-1.651 15.667 1.795 35.768 8.288 47.403 15.313 17.926 10.821 35.234 28.667 44.544 45.926 8.197 15.197 13.298 33.013 14.584 50.942.694 9.675.694 9.676-2.083 12.25-3.451 3.198-7.411 3.299-10.676.274-2.167-2.009-2.563-3.446-3.123-11.323-1.894-26.694-11.898-48.999-30.304-67.567-18.082-18.242-41.047-28.601-67.577-30.484-7.315-.52-9.268-1.025-11.323-2.929-3.232-2.996-3.244-7.284-.031-10.751m-48-80C4.086 4.883 5.937 4 8 4c2.063 0 3.914.883 5.548 2.646L16 9.292V126.646 244H133.548 251.095l2.453 2.646c3.278 3.538 3.204 7.753-.194 10.902L250.708 260H128 5.292L2.646 257.548 0 255.095V132.194 9.292l2.452-2.646"></Path>
                  </Svg>
                  <Text style={styles.filterListItemLeftTest}>
                    {strings.filter.living_area}:
                  </Text>
                  <View>
                    <Text style={styles.filterListItemRightTest}>
                      {`${marketData.filterData.living_area_min}-${marketData.filterData.living_area_max} ${strings.filter.living_area_unit}`}
                    </Text>
                  </View>
                </View>
              )}
              {marketData.filterData.monthly_fee_max && (
                <View style={styles.filterListItemStyles}>
                  <Svg
                    width="10"
                    height="10"
                    viewBox="0 0 256 256"
                    style={{ transform: 'translateY(-2px)' }}>
                    <Path
                      fill="#64748b"
                      d="M200.4 173.808C200.4 136.512 170.608 123.616 144 115.76V58.928C151.981 60.3578 159.764 62.7318 167.184 66L181.488 73.072L195.664 44.368L181.264 37.296C169.421 31.9635 156.87 28.3691 144 26.624V0H112V26.704C78.176 31.792 56.4 52.8 56.4 82.176C56.4 119.088 85.6 132.096 112 139.984V197.584C98.6274 196.41 85.4476 193.61 72.752 189.248L57.696 183.808L46.832 213.904L61.888 219.344C78.0622 225.021 94.9088 228.56 112 229.872V256H144V229.488C194.416 223.296 200.4 189.232 200.4 173.808ZM88.4 82.176C88.4 67.68 100.624 61.648 112 59.2V106.32C95.808 100.56 88.4 94.4 88.4 82.176ZM144 197.232V149.36C160.8 155.2 168.4 161.344 168.4 173.808C168.4 179.472 168.304 193.008 144 197.232Z"></Path>
                  </Svg>
                  <Text style={styles.filterListItemLeftTest}>
                    {strings.filter.monthly_fee}:
                  </Text>
                  <View>
                    <Text style={styles.filterListItemRightTest}>
                      {`${marketData.filterData.monthly_fee_min}-${marketData.filterData.monthly_fee_max} ${strings.filter.monthly_fee_unit}`}
                    </Text>
                  </View>
                </View>
              )}
              {marketData.filterData.construction_year_max && (
                <View style={styles.filterListItemStyles}>
                  <Svg
                    width="10"
                    height="10"
                    viewBox="0 0 256 256"
                    style={{ transform: 'translateY(-2px)' }}>
                    <Path
                      fill="#64748b"
                      d="M256 80V48L96 32V0H48V32L0 48V80H48V224.096H0V256H144V224.096H96V80H256Z M224 144.096V96H208V144.096H192C187.757 144.096 183.687 145.782 180.686 148.782C177.686 151.783 176 155.853 176 160.096V192C176 196.243 177.686 200.313 180.686 203.314C183.687 206.314 187.757 208 192 208H240C244.243 208 248.313 206.314 251.314 203.314C254.314 200.313 256 196.243 256 192V160.096C256 155.853 254.314 151.783 251.314 148.782C248.313 145.782 244.243 144.096 240 144.096H224Z"></Path>
                  </Svg>
                  <Text style={styles.filterListItemLeftTest}>
                    {strings.filter.construction_year}:
                  </Text>
                  <View>
                    <Text style={styles.filterListItemRightTest}>
                      {`${marketData.filterData.construction_year_min}-${marketData.filterData.construction_year_max}`}
                    </Text>
                  </View>
                </View>
              )}
              <Text>{' '}</Text>
              {marketData.filterData.rooms_max && (
                <View style={styles.filterListItemStyles}>
                  <Svg
                    width="10"
                    height="10"
                    viewBox="0 0 256 256"
                    style={{ transform: 'translateY(-2px)' }}>
                    <Path
                      fill="#64748b"
                      d="M80 112V72C80 69.8783 80.8429 67.8434 82.3431 66.3431C83.8434 64.8429 85.8783 64 88 64H168C170.122 64 172.157 64.8429 173.657 66.3431C175.157 67.8434 176 69.8783 176 72V112H224V32C224 27.7565 222.314 23.6869 219.314 20.6863C216.313 17.6857 212.243 16 208 16H48C43.7565 16 39.6869 17.6857 36.6863 20.6863C33.6857 23.6869 32 27.7565 32 32V112H80Z M240 144H16C11.7565 144 7.68687 145.686 4.68629 148.686C1.68571 151.687 0 155.757 0 160V224C0 228.243 1.68571 232.313 4.68629 235.314C7.68687 238.314 11.7565 240 16 240C20.2435 240 24.3131 238.314 27.3137 235.314C30.3143 232.313 32 228.243 32 224V208H224V224C224 228.243 225.686 232.313 228.686 235.314C231.687 238.314 235.757 240 240 240C244.243 240 248.313 238.314 251.314 235.314C254.314 232.313 256 228.243 256 224V160C256 155.757 254.314 151.687 251.314 148.686C248.313 145.686 244.243 144 240 144Z"></Path>
                  </Svg>
                  <Text style={styles.filterListItemLeftTest}>
                    {strings.filter.rooms}:
                  </Text>
                  <View>
                    <Text style={styles.filterListItemRightTest}>
                      {`${marketData.filterData.rooms_min}-${marketData.filterData.rooms_max} ${strings.filter.rooms_unit}`}
                    </Text>
                  </View>
                </View>
              )}
            </View>
          </View>
          {marketImage && (
            <Image src={marketImage} style={{ marginTop: '20px' }} />
          )}
        </View>
        <View style={{ width: '50%' }}>
          <View>
            <Text
              style={{
                fontWeight: 500,
                fontFamily: 'Chivo',
                fontSize: '20px',
              }}>
              {strings.navigation.market}
            </Text>
          </View>
          <View>
            <View>
              {listData.map(({ firm, market_share }, index) => (
                <View
                  key={index}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    padding: '8px',
                    margin: '0',
                    marginTop: '4px',
                    justifyContent: 'space-between',
                  }}>
                  <View
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      borderRadius: '8px',
                      opacity: 0.1,
                      borderWidth: '1px',
                      backgroundColor: `rgba(${colorsTop10[index]}, 0.2)`,
                      borderColor: `rgba(${colorsTop10[index]}, 0.6)`,
                    }}></View>
                  <View
                    style={{
                      borderRadius: '9999px',
                      height: '16px',
                      width: '16px',
                      backgroundColor: `rgba(${colorsTop10[index]}, 0.6)`,
                      borderColor: `rgba(${colorsTop10[index]}, 1)`,
                    }}></View>
                  <Text
                    style={{
                      fontSize: '11px',
                      fontFamily: 'Chivo',
                      fontWeight: 400,
                      paddingLeft: '8px',
                      flex: '1 1 0%',
                    }}>
                    {firm === 'Other' ? strings.market.othersLabel : firm}
                  </Text>
                  <Text
                    style={{
                      fontSize: '11px',
                      fontFamily: 'Chivo',
                      fontWeight: 400,
                    }}>{`${formatNumber(market_share)}%`}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>
      <Footer hideClientData={hideClientData} username={username}/>
    </Page>
  );
};

export default MarketsPage;
